import {
    getApplication,
    listApplications
} from './queries';
import {
    createApplication,
    deleteApplication,
    deprovisionApplication,
    markApplicationDecommissioned,
    provisionApplication,
    updateApplication,
} from './mutations';

import {GraphQLQuery,} from '@aws-amplify/api';

import {Application, ApplicationStageStringMap} from "../types.ts";
import {generateClient} from "aws-amplify/api";


export type ListApplications = {
    listApplications: { items: Application[], nextToken: string | null }
}

export type GetApplication = {
    getApplication: Application
}

export type CreateApplication = {
    createApplication: Application
}

export type UpdateApplication = {
    updateApplication: Application
}

export type DeleteApplication = {
    deleteApplication: string
}

// FNP-1013: the provisioning execution. Same shape as the teardown result
// below, because both are "a workflows-api Execution is now driving this".
export type ProvisionApplicationResult = {
    applicationId: string
    executionId: string
    sfnExecutionArn?: string | null
    executionStatus?: string | null
}

export type ProvisionApplication = {
    provisionApplication: ProvisionApplicationResult
}

// FNP-998: the teardown execution, returned so the console can subscribe to
// progress on the same workflows-api machinery the create flow uses.
export type DeprovisionApplicationResult = {
    applicationId: string
    executionId: string
    sfnExecutionArn?: string | null
    executionStatus?: string | null
}

export type DeprovisionApplication = {
    deprovisionApplication: DeprovisionApplicationResult
}

// FNP-1007: the outcome of a mark-decommissioned attempt.
//
// ⚠️ `found` and `incompleteChecks` are SEPARATE fields carrying SEPARATE
// meanings, and must stay that way all the way to the screen. `found` is proof
// that resources exist; `incompleteChecks` is proof of nothing at all. From the
// console those two situations are otherwise indistinguishable — the sweep is
// the only thing that tells them apart — so collapsing them into one "it
// failed" shape here would destroy the distinction the backend exists to
// provide.
export type FoundResource = {
    resourceType: string
    name: string
}

export type IncompleteCheck = {
    resourceType: string
    // ACCESS_DENIED | THROTTLED | TRUNCATED | ERROR
    failureKind?: string | null
    failureReason?: string | null
}

export type MarkApplicationDecommissionedResult = {
    applicationId: string
    // True only when all four sweeps completed and all four were empty.
    marked: boolean
    // Set when marked: the record's new identity, freeing the original name.
    name?: string | null
    originalName?: string | null
    decommissionedAt?: string | null
    decommissionMethod?: string | null
    // Why it refused. Always set when `marked` is false.
    reason?: string | null
    found?: FoundResource[] | null
    incompleteChecks?: IncompleteCheck[] | null
}

export type MarkApplicationDecommissioned = {
    markApplicationDecommissioned: MarkApplicationDecommissionedResult
}

const client = generateClient();


// FNP-913: returns the CONNECTION, not just items. `nextToken` is what
// useInfiniteQuery pages on; returning `.items` alone (as this did) made token
// pagination structurally impossible no matter what the hook asked for.
export const fetchRecords = async (
    filter: object,
    limit?: number,
    nextToken?: string | null
) => {
    const response = await client.graphql<GraphQLQuery<ListApplications>>({
                                                                              query: listApplications,
                                                                              variables: {
                                                                                  filter,
                                                                                  limit,
                                                                                  nextToken
                                                                              },
                                                                          });
    return {
        items: response.data?.listApplications?.items ?? [],
        nextToken: response.data?.listApplications?.nextToken ?? null,
    };
};

export const fetch = async (id: string) => {
    const response = await client.graphql<GraphQLQuery<GetApplication>>({
                                                                            query: getApplication,
                                                                            variables: {id: id},
                                                                        });
    return response.data?.getApplication;
};

export const add = async (payload: object) => {
    const response = await client.graphql<GraphQLQuery<CreateApplication>>({
                                                                               query: createApplication,
                                                                               variables: {input: payload},
                                                                           });
    return response.data?.createApplication;
};

// FNP-1035: build the updateApplication input by WHITELISTING only the fields
// UpdateApplicationInput defines. The previous approach blacklisted two known
// output fields off the loaded Application record (delete record.created,
// delete record.updated) and shipped everything else the read selection set
// hydrates — stageDomains, stageHostedZoneIds, stageCertificateArns, workflowId,
// stepFunctionsExecutionArn, statusReason, statusUpdatedAt, progress,
// rollbackSummary, the FNP-1016 `infrastructure` relation, and a `__typename` on
// every nested object. AppSync rejected the whole mutation ("The variables input
// contains a field that is not defined for input object type
// 'UpdateApplicationInput'"), so editing ANY application was broken — including
// the only recovery path for a stranded one.
//
// Whitelisting is the platform convention (build a fresh input from canonical
// fields only — see buildUpdatePipelineInput in devops/pipelines, FNP-545) and
// is immune to new Application output fields appearing later.
//
// A key is included only when the record actually carries a value, so fields the
// user never touched are omitted rather than sent as an explicit `undefined`.
const putIfPresent = <T>(input: Record<string, unknown>, key: string, value: T | null | undefined) => {
    if (value !== undefined && value !== null) {
        input[key] = value;
    }
};

// ⚠️ `name` is deliberately ABSENT from this whitelist even though
// UpdateApplicationInput declares it.
//
// FNP-176 made the application name immutable and added a resolver guard that
// rejects the field's mere PRESENCE — console-api
// graphql/functions/applications/update.ts throws `NameImmutable` on
// `input.name !== undefined`, regardless of whether the value changed. The
// schema separately required `name: String!` until 2026-08-16, which made EVERY
// updateApplication fail in prod until it was relaxed to optional. Sending
// `name` here reintroduces that outage. The name is the foreign-key shadow for
// the provisioned resources; the pointers below are repairable, the identity is
// not.
export const buildUpdateApplicationInput = (record: Application) => {
    const input: Record<string, unknown> = {id: record.id};

    putIfPresent(input, 'description', record.description);
    putIfPresent(input, 'url', record.url);
    putIfPresent(input, 'logoUrl', record.logoUrl);
    putIfPresent(input, 'applicationStatus', record.applicationStatus);
    putIfPresent(input, 'applicationCategory', record.applicationCategory);
    putIfPresent(input, 'displayName', record.displayName);
    putIfPresent(input, 'parentDomainName', record.parentDomainName);
    putIfPresent(input, 'subdomain', record.subdomain);
    // FNP-1489: repairable like the resource pointers below — a mistyped zone
    // id must be fixable from the console, not by writing to DynamoDB.
    putIfPresent(input, 'hostedZoneId', record.hostedZoneId);
    putIfPresent(input, 'hostedZoneName', record.hostedZoneName);
    putIfPresent(input, 'certificateArn', record.certificateArn);
    putIfPresent(input, 'deploymentAccountGroupId', record.deploymentAccountGroupId);
    putIfPresent(input, 'tags', record.tags?.map((t) => ({key: t.key, value: t.value})));
    putIfPresent(input, 'sourceRepositoryName', record.sourceRepositoryName);
    putIfPresent(input, 'infraRepositoryName', record.infraRepositoryName);
    putIfPresent(input, 'sourcePipelineId', record.sourcePipelineId);
    putIfPresent(input, 'infraPipelineId', record.infraPipelineId);
    // ⚠️ StageStringMapInput's dev/test/prod are non-null: a partial write makes
    // AppSync null the WHOLE object on read. The stage keys are passed through
    // as the form submitted them rather than being padded.
    putIfPresent(input, 'infraStackNames', toStageStringMapInput(record.infraStackNames));
    // FNP-1066: same shape, same hazard, same helper as `infraStackNames`
    // directly above. The reader (Application.deploymentBucketNames + the
    // getApplication selection set) landed with this line — the two are useless
    // apart, which is why the placeholder note here said to add them together.
    putIfPresent(input, 'deploymentBucketNames', toStageStringMapInput(record.deploymentBucketNames));

    return input;
};

// Strips __typename off the stage maps — the read selection set hydrates it on
// every nested object and StageStringMapInput does not define it.
//
// ⚠️ It picks the three stage keys rather than PADDING absent ones: a map that
// arrives without `test` yields `{dev, test: undefined, prod}`, and
// `putIfPresent` still sends it because the map itself is present. That is
// deliberate — padding would invent an empty-string bucket/stack name for an
// environment the caller said nothing about, and an empty name is exactly what
// makes the teardown skip. Submitting all three keys is the FORM's job (see
// ProvisioningMetadataForm), not this helper's.
//
// Shared by `infraStackNames` and `deploymentBucketNames` (FNP-1066) so both
// stage maps are encoded one way. Typed off the field rather than the record so
// it is not read as belonging to either one.
const toStageStringMapInput = (value: ApplicationStageStringMap | null | undefined) =>
    value === undefined || value === null
        ? value
        : {dev: value.dev, test: value.test, prod: value.prod};

export const update = async (_id: string, record: Application) => {
    const input = buildUpdateApplicationInput(record);

    const response = await client.graphql<GraphQLQuery<UpdateApplication>>({
                                                                               query: (updateApplication),
                                                                               variables: {input},
                                                                           });
    return response.data?.updateApplication;
};

// FNP-1013: build an application's infrastructure, using the playbook the
// admin chose.
//
// ⚠️ NOT part of `addRecord`. Creating the record and building the
// infrastructure are separate acts, and this is the second one — an
// application can exist as a record indefinitely without ever being
// provisioned. That separation is the point of FNP-1013.
//
// ⚠️ `playbookId` is passed, never derived. Deriving it from the application's
// name or type would be the naming-convention lookup the owner explicitly
// rejected: a hardcoded string swapped for a conventional one is still
// brittle. The admin picks from the playbooks that exist in THIS environment.
//
// Validation of the choice belongs to workflows-api — it already checks the
// playbook exists, is ACTIVE, has a stateMachineArn and satisfies its own
// inputSchema. Do not re-check any of that here; that is the cross-boundary
// validation the platform rules forbid.
// FNP-1499: `businessDomain` and `applicationType` USED TO BE SENT HERE.
//
// Both are gone. The playbook required them and nothing read them, so the
// dialog was asking the operator to re-answer two dead questions it had
// already asked on the create form. The playbook stopped requiring them
// first, so removing them here cannot cause a refusal.
export const provision = async (
    id: string,
    playbookId: string,
    stage: string,
    subdomain?: string,
    deploymentAccountId?: string,
) => {
    const input: Record<string, string> = {id, playbookId, stage};

    // FNP-1231: sent only when non-empty. An empty subdomain is a REAL choice
    // (apex deployment, FNP-287) but it is the record's fallback that should
    // express it, not an empty string travelling through the mutation.
    if (subdomain) {
        input.subdomain = subdomain;
    }

    // FNP-1518: sent only when non-empty, for the same reason as the subdomain
    // above. Absent means "use the playbook's account", which is what every
    // existing application relies on; an empty string would travel intact and
    // read as a deliberate choice of no account at all.
    if (deploymentAccountId) {
        input.deploymentAccountId = deploymentAccountId;
    }

    const response = await client.graphql<GraphQLQuery<ProvisionApplication>>({
                                                                                 query: (provisionApplication),
                                                                                 variables: {input},
                                                                             });
    return response.data?.provisionApplication;
};

// FNP-998: start an infrastructure teardown.
//
// ⚠️ NOT `deleteRecord`. That deletes the Application row and touches no AWS
// resources; this destroys the resources and keeps the row. Calling the wrong
// one either orphans live infrastructure or leaves a decommissioned app in the
// list — hence the deliberately different name and the explicit `stage`.
export const deprovision = async (id: string, stage: string) => {
    const response = await client.graphql<GraphQLQuery<DeprovisionApplication>>({
                                                                                   query: (deprovisionApplication),
                                                                                   variables: {input: {id, stage}},
                                                                               });
    return response.data?.deprovisionApplication;
};

// FNP-1007: ask the server to PROVE the infrastructure is gone and, only if
// the proof holds, correct the record to DECOMMISSIONED.
//
// ⚠️ Destroys nothing. Contrast `deprovision` directly above, which destroys
// everything. The two are one line apart on purpose: the difference is the
// whole point, and a reader picking one should see the other.
//
// FNP-1035: the payload is an explicit allow-list — `{id}` and nothing else.
// The record is NOT spread in. MarkApplicationDecommissionedInput defines `id`
// only, and AppSync rejects the entire mutation when it receives a field the
// input type does not declare, so spreading a loaded Application (which
// hydrates __typename, progress, infrastructure, and the stage maps) would fail
// every call. There is deliberately no `stage` key: the schema does not define
// one, and the sweep is application-wide.
export const markDecommissioned = async (id: string) => {
    const response = await client.graphql<GraphQLQuery<MarkApplicationDecommissioned>>({
                                                                                           query: (markApplicationDecommissioned),
                                                                                           variables: {input: {id}},
                                                                                       });
    return response.data?.markApplicationDecommissioned;
};

export const deleteRecord = async (id: string) => {
    const response = await client.graphql<GraphQLQuery<DeleteApplication>>({
                                                                               query: (deleteApplication),
                                                                               variables: {input: {id: id}},
                                                                           });
    return response.data?.deleteApplication;
};
